import React, { useRef, useState, useEffect } from "react";
import { Avatar } from "./Avatar";
import { CustomIcon } from "./CustomIcon";
import PhotoCropModal from "./PhotoCropModal";

// ============================================================
// Compression config — all sizes are LIMITS, not targets.
// ============================================================
const MAX_DIMENSION = 400;          // px — max width or height; aspect ratio kept
const MAX_OUTPUT_BYTES = 200 * 1024; // 200 KB — hard ceiling on the resulting base64 payload
const WARNING_BYTES = 2 * 1024 * 1024;   // 2 MB — show gold warning if original is larger
const HARD_UPLOAD_LIMIT = 5 * 1024 * 1024; // 5 MB — reject files larger than this
const QUALITY_STEPS = [0.85, 0.78, 0.70, 0.62, 0.55, 0.48, 0.40, 0.34, 0.28, 0.22, 0.18, 0.14, 0.10];

// Returns approximate byte size of a base64 data URL's *encoded* payload.
function dataUrlBytes(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return 0;
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return dataUrl.length;
  const b64 = dataUrl.slice(comma + 1);
  // base64 → bytes: every 4 chars encode 3 bytes; ignore "=" padding.
  const padding = (b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0);
  return Math.floor(b64.length * 3 / 4) - padding;
}

// Load any data URL (or blob URL) into an HTMLImageElement.
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e || new Error("Failed to decode image"));
    img.src = src;
  });
}

// Compute target dimensions while preserving aspect ratio.
function fitDimensions(w, h, maxDim) {
  if (w <= maxDim && h <= maxDim) return { w, h };
  const ratio = w / h;
  if (w >= h) return { w: maxDim, h: Math.round(maxDim / ratio) };
  return { w: Math.round(maxDim * ratio), h: maxDim };
}

// Canvas-based compression: resize to ≤ MAX_DIMENSION, then iteratively
// step the JPEG quality down until the result is ≤ MAX_OUTPUT_BYTES.
// Returns the data URL string.
export async function compressImageDataUrl(input, opts = {}) {
  const maxDim = opts.maxDimension ?? MAX_DIMENSION;
  const maxBytes = opts.maxBytes ?? MAX_OUTPUT_BYTES;
  const img = await loadImage(input);
  const { w, h } = fitDimensions(img.naturalWidth || img.width, img.naturalHeight || img.height, maxDim);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  // White background so JPEG doesn't render transparent pixels as black.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  // Try descending quality until we fit the byte budget.
  let lastUrl = null;
  for (const q of QUALITY_STEPS) {
    const url = canvas.toDataURL("image/jpeg", q);
    lastUrl = url;
    if (dataUrlBytes(url) <= maxBytes) return url;
  }
  // Still too large at minimum quality — shrink dimensions further and retry once.
  if (maxDim > 200) {
    return compressImageDataUrl(input, { maxDimension: Math.floor(maxDim * 0.75), maxBytes });
  }
  return lastUrl; // best effort
}

// File → compressed base64 data URL. Used by the upload flow.
async function compressFile(file) {
  const reader = new FileReader();
  const dataUrl = await new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result);
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
  return compressImageDataUrl(dataUrl);
}

export function PhotoUploader({ value, name, onChange, size = 84, "data-testid": testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState("");
  const [info, setInfo] = useState("");
  // Cropping flow — raw selected image stored here while modal is open.
  const [cropSrc, setCropSrc] = useState(null);

  const handleFile = async (file) => {
    setWarning(""); setInfo("");
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please choose an image file.");
      return;
    }
    if (file.size > HARD_UPLOAD_LIMIT) {
      alert(`Image is ${(file.size / 1024 / 1024).toFixed(1)} MB. Maximum upload size is 5 MB.`);
      return;
    }
    if (file.size > WARNING_BYTES) {
      setWarning(
        `This image is large (${(file.size / 1024 / 1024).toFixed(1)} MB) and will be compressed automatically for optimal performance.`
      );
    }
    // Read into data URL → open crop modal.
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.onerror = () => alert("Could not read this image.");
    reader.readAsDataURL(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  // Called by PhotoCropModal once the user confirms the crop.
  const handleCropped = async (croppedDataUrl) => {
    setBusy(true);
    try {
      const compressed = await compressImageDataUrl(croppedDataUrl);
      const afterKB = (dataUrlBytes(compressed) / 1024).toFixed(0);
      setInfo(`Compressed to ${afterKB} KB`);
      setCropSrc(null);
      onChange(compressed);
    } catch (e) {
      console.error("Image compression failed:", e);
      alert("Failed to process this image. Please try a different file.");
    } finally {
      setBusy(false);
    }
  };

  // Migration — recompress legacy oversized photos the first time they
  // are mounted in an editable PhotoUploader. The new compressed value
  // is propagated via onChange so the parent persists it on next save.
  useEffect(() => {
    let cancelled = false;
    if (typeof value === "string" && value.startsWith("data:image/") && dataUrlBytes(value) > MAX_OUTPUT_BYTES) {
      (async () => {
        try {
          const out = await compressImageDataUrl(value);
          if (!cancelled && dataUrlBytes(out) < dataUrlBytes(value)) {
            onChange(out);
            setInfo("Legacy photo recompressed to fit the new 200 KB limit. Save to persist.");
          }
        } catch {
          /* keep original if migration fails */
        }
      })();
    }
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="flex items-center gap-12" style={{ alignItems: "flex-start" }}>
      <Avatar
        name={name}
        photo={value}
        style={{
          width: size, height: size,
          fontSize: Math.max(14, Math.floor(size * 0.32)),
          border: "2px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
        }}
      />
      <div className="flex flex-col gap-8" style={{ flexDirection: "column", display: "flex", gap: 8, minWidth: 0, flex: 1 }}>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          className="btn btn-sm btn-outline"
          data-testid={testId ? `${testId}-upload` : undefined}
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {busy ? "Compressing…" : value ? "Change photo" : <><CustomIcon name="camera-upload" size={14} /> Upload photo</>}
        </button>
        {value && (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            data-testid={testId ? `${testId}-remove` : undefined}
            disabled={busy}
            onClick={() => onChange(null)}
          >
            Remove
          </button>
        )}
        {warning && (
          <div
            data-testid={testId ? `${testId}-warning` : "photo-uploader-warning"}
            style={{
              background: "color-mix(in srgb, var(--kc-accent) 12%, transparent)",
              border: "1px solid color-mix(in srgb, var(--kc-accent) 50%, transparent)",
              color: "var(--kc-accent)",
              padding: "6px 10px",
              borderRadius: 6,
              fontSize: 11,
              lineHeight: 1.4,
              maxWidth: 280,
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="warning" size={12} /> {warning}</span>
          </div>
        )}
        {info && (
          <div
            data-testid={testId ? `${testId}-info` : "photo-uploader-info"}
            style={{
              color: "var(--kc-sub)",
              fontSize: 11,
              lineHeight: 1.4,
              maxWidth: 280,
            }}
          >
            {info}
          </div>
        )}
      </div>
      {cropSrc && (
        <PhotoCropModal
          src={cropSrc}
          onCancel={() => { setCropSrc(null); setWarning(""); }}
          onCropped={handleCropped}
        />
      )}
    </div>
  );
}
